import types from '../mutation-types';
import AudienceListsAPI from '../../api/audienceLists';

export const state = {
  records: [],
  members: {},
  activeImports: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isImporting: false,
    isFetchingMembers: false,
    hasError: false,
  },
};

const emptyMembers = { payload: [], count: 0, currentPage: 1 };

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAudienceLists(_state) {
    return _state.records;
  },
  getAudienceList: _state => id =>
    _state.records.find(record => record.id === Number(id)) || null,
  getMembers: _state => id => _state.members[id] || emptyMembers,
  getActiveImport: _state => id => _state.activeImports[id] || null,
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_AUDIENCE_LIST_UI_FLAG, {
      isFetching: true,
      hasError: false,
    });
    try {
      const { data } = await AudienceListsAPI.get();
      commit(types.SET_AUDIENCE_LISTS, data.payload ?? []);
    } catch (error) {
      // A rejected fetch used to escape as an unhandled rejection and leave the
      // page on its *empty* state, which reads as "this account has no
      // audiences" rather than "we could not load them".
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { hasError: true });
    } finally {
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isFetching: false });
    }
  },
  create: async ({ commit }, payload) => {
    commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isCreating: true });
    try {
      const { data } = await AudienceListsAPI.create({
        audience_list: payload,
      });
      commit(types.ADD_AUDIENCE_LIST, data.payload);
      return data.payload;
    } finally {
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...payload }) => {
    commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await AudienceListsAPI.update(id, {
        audience_list: payload,
      });
      commit(types.EDIT_AUDIENCE_LIST, data.payload);
      return data.payload;
    } finally {
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isDeleting: true });
    try {
      await AudienceListsAPI.delete(id);
      commit(types.DELETE_AUDIENCE_LIST, id);
    } finally {
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isDeleting: false });
    }
  },
  show: async ({ commit }, id) => {
    const { data } = await AudienceListsAPI.show(id);
    commit(types.EDIT_AUDIENCE_LIST, data.payload);
    return data.payload;
  },
  previewFile: async (_context, file) => {
    const { data } = await AudienceListsAPI.previewFile(file);
    return data;
  },
  createImport: async (
    { commit },
    { id, file, columnMapping, defaultCountryCode }
  ) => {
    commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isImporting: true });
    try {
      const { data } = await AudienceListsAPI.createImport(id, {
        file,
        columnMapping,
        defaultCountryCode,
      });
      commit(types.SET_AUDIENCE_LIST_ACTIVE_IMPORT, {
        id,
        importRecord: data.payload,
      });
      return data.payload;
    } finally {
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isImporting: false });
    }
  },
  registryPreview: async (_context, filters) => {
    const { data } = await AudienceListsAPI.registryPreview(filters);
    return data;
  },
  candidatePreview: async (_context, payload) => {
    const { data } = await AudienceListsAPI.candidatePreview(payload);
    return data.payload;
  },
  candidateFilterOptions: async () => {
    const { data } = await AudienceListsAPI.candidateFilterOptions();
    return data.payload;
  },
  createRegistryImport: async (
    { commit },
    { id, filters, defaultCountryCode }
  ) => {
    commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isImporting: true });
    try {
      const { data } = await AudienceListsAPI.createRegistryImport(id, {
        filters,
        defaultCountryCode,
      });
      commit(types.SET_AUDIENCE_LIST_ACTIVE_IMPORT, {
        id,
        importRecord: data.payload,
      });
      return data.payload;
    } finally {
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isImporting: false });
    }
  },
  createCandidateImport: async (
    { commit },
    { id, filters, selectionLimit, defaultCountryCode }
  ) => {
    commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isImporting: true });
    try {
      const { data } = await AudienceListsAPI.createCandidateImport(id, {
        filters,
        selectionLimit,
        defaultCountryCode,
      });
      commit(types.SET_AUDIENCE_LIST_ACTIVE_IMPORT, {
        id,
        importRecord: data.payload,
      });
      return data.payload;
    } finally {
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isImporting: false });
    }
  },
  // The import runs in Sidekiq, so the page polls until the record leaves the
  // pending/processing states and then refreshes the list counters.
  pollImport: async ({ commit, dispatch }, { id, importId }) => {
    const { data } = await AudienceListsAPI.importStatus(id, importId);
    commit(types.SET_AUDIENCE_LIST_ACTIVE_IMPORT, {
      id,
      importRecord: data.payload,
    });
    const isSettled = ['completed', 'failed'].includes(data.payload.status);
    if (isSettled) await dispatch('show', id);
    return data.payload;
  },
  fetchMembers: async ({ commit }, { id, page = 1, status, query }) => {
    commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isFetchingMembers: true });
    try {
      const { data } = await AudienceListsAPI.members(id, {
        page,
        status,
        query,
      });
      commit(types.SET_AUDIENCE_LIST_MEMBERS, {
        id,
        payload: data.payload,
        count: data.meta?.count ?? 0,
        currentPage: data.meta?.current_page ?? page,
      });
      return data.payload;
    } finally {
      commit(types.SET_AUDIENCE_LIST_UI_FLAG, { isFetchingMembers: false });
    }
  },
};

export const mutations = {
  [types.SET_AUDIENCE_LIST_UI_FLAG](_state, uiFlags) {
    _state.uiFlags = { ..._state.uiFlags, ...uiFlags };
  },
  [types.SET_AUDIENCE_LISTS](_state, records) {
    _state.records = records;
  },
  [types.ADD_AUDIENCE_LIST](_state, record) {
    _state.records = [record, ..._state.records];
  },
  [types.EDIT_AUDIENCE_LIST](_state, record) {
    const index = _state.records.findIndex(item => item.id === record.id);
    if (index === -1) {
      _state.records = [record, ..._state.records];
      return;
    }
    _state.records.splice(index, 1, { ..._state.records[index], ...record });
  },
  [types.DELETE_AUDIENCE_LIST](_state, id) {
    _state.records = _state.records.filter(record => record.id !== id);
  },
  [types.SET_AUDIENCE_LIST_MEMBERS](
    _state,
    { id, payload, count, currentPage }
  ) {
    _state.members = {
      ..._state.members,
      [id]: { payload, count, currentPage },
    };
  },
  [types.SET_AUDIENCE_LIST_ACTIVE_IMPORT](_state, { id, importRecord }) {
    _state.activeImports = { ..._state.activeImports, [id]: importRecord };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
